// Files tab.
//
// Lists every downloaded artifact (PDF / XLSX / CSV) the agent pulled
// from a merchant. Each row has an "Extract information" button that
// pushes the file through the document pipeline; the resulting structured
// invoice then appears in the Documents tab.
//
// Status semantics:
//   not_extracted   — file landed, hasn't been processed yet
//   extracted       — pipeline ran cleanly, structured doc exists
//   needs_review    — pipeline ran, validation flagged issues
//   extract_failed  — pipeline raised; doc row exists with the error
//   corrected       — human edited the extracted fields after the fact
//
// All filters point to /finance/rpa/files which is team-scoped via the
// artifact's parent run.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Stack, Typography, Button, IconButton, Chip, Tooltip,
    Table, TableHead, TableBody, TableRow, TableCell, TextField,
    Alert, CircularProgress, Select, FormControl, InputLabel, MenuItem,
    LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions,
    FormControlLabel, Switch, Divider, List, ListItem, ListItemText,
    ListItemIcon, ListItemSecondaryAction,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ReplayIcon from '@mui/icons-material/Replay';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import LaunchIcon from '@mui/icons-material/Launch';
import ScheduleIcon from '@mui/icons-material/Schedule';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import SearchIcon from '@mui/icons-material/Search';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import {
    rpaFileList, rpaFileExtract, rpaFileDelete, rpaFileUrl,
    rpaFileUpload, rpaMerchantList,
} from './api';
import {
    KpiCard, KpiGrid, SectionPaper, TableShell, EmptyState, Toolbar,
    StatusChip, RPA_COLORS, formatDateLong, formatBytes,
} from './rpaTheme';


// Map the canonical extracted-doc statuses to chip semantics. "not_extracted"
// is our synthetic state — the artifact exists but no pipeline run yet.
const STATUS_TONE = {
    not_extracted:  { label: 'Not extracted', color: 'default',
                      icon: <HelpOutlineIcon sx={{ fontSize: 14 }} /> },
    extracted:      { label: 'Extracted',     color: 'success',
                      icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    corrected:      { label: 'Corrected',     color: 'info',
                      icon: <CheckCircleIcon sx={{ fontSize: 14 }} /> },
    needs_review:   { label: 'Needs review',  color: 'warning',
                      icon: <WarningAmberIcon sx={{ fontSize: 14 }} /> },
    extract_failed: { label: 'Extract failed',color: 'error',
                      icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} /> },
    superseded:     { label: 'Superseded',    color: 'default',
                      icon: <HelpOutlineIcon sx={{ fontSize: 14 }} /> },
};


/**
 * Build a human-readable reason string explaining the current status
 * of an artifact's extraction. Used as the tooltip on the status chip
 * so the user can see WHY a row is in its current state without
 * jumping to the Documents tab.
 *
 * For NOT_EXTRACTED rows we have no extraction record to read; the
 * reason is always "no pipeline pass has run yet" plus a hint at what
 * the user should do next.
 *
 * For EXTRACT_FAILED rows the reason comes from `error_message` on
 * the linked RPAExtractedDocument (set by document_pipeline.py — covers
 * parser exceptions, Gemini quota / API errors, "no text in PDF",
 * etc.).
 *
 * For NEEDS_REVIEW rows the reason is the list of validation issues
 * (canonical-field validation rules — missing amount, mismatched
 * line-item sum, suspect currency, etc.).
 */
function extractionReason(file) {
    const statusKey = file.extraction
        ? file.extraction.status
        : 'not_extracted';
    const fmt = file.extraction?.format;
    const err = file.extraction?.error_message;
    const issues = file.extraction?.validation_issues;

    if (statusKey === 'not_extracted') {
        // Common case for new arrivals: the file landed but the
        // pipeline either hasn't run yet (e.g. crashed mid-run) or
        // was never kicked. Tell the user what to do next.
        return ('This file is in the Files tab but hasn\'t been '
              + 'processed by the document pipeline yet. Click the '
              + 'Extract button to the right to attempt extraction '
              + 'now — usually a one-click fix.');
    }
    if (statusKey === 'extract_failed') {
        if (err) {
            // Trim and de-noise — error_message can be a full Python
            // traceback. Show the first 280 chars; the full message
            // is still in the audit log if the user needs it.
            const trimmed = String(err).trim();
            return ('Extraction failed: '
                + (trimmed.length > 280
                    ? trimmed.slice(0, 280) + '…'
                    : trimmed));
        }
        // No explicit error_message but the row was marked failed.
        // Usually means the extractor returned no canonical fields
        // (e.g. PDF had no extractable text and no scanned-image
        // fallback worked).
        return ('Extraction failed: the pipeline couldn\'t pull any '
            + 'canonical fields (amount, date, reference, vendor) '
            + 'from this file. '
            + (fmt
                ? `Detected format was "${fmt}".`
                : 'The file format may not be supported, or the '
                  + 'content is unreadable.'));
    }
    if (statusKey === 'needs_review') {
        const list = Array.isArray(issues) ? issues : [];
        if (list.length === 0) {
            return ('Extracted but flagged for review — see the '
                + 'Documents tab for details.');
        }
        return ('Extracted but flagged for review:\n• '
            + list.slice(0, 5).join('\n• ')
            + (list.length > 5 ? `\n+${list.length - 5} more…` : ''));
    }
    if (statusKey === 'extracted' || statusKey === 'corrected') {
        return 'Extraction completed cleanly.';
    }
    if (statusKey === 'superseded') {
        return ('A newer extraction has replaced this one. Open the '
            + 'Documents tab for the current version.');
    }
    return '';
}


export default function FileList({ onJumpToDocuments }) {
    const [files, setFiles] = useState([]);
    const [counts, setCounts] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [extracting, setExtracting] = useState(null); // artifact id in flight
    const [confirmDelete, setConfirmDelete] = useState(null);

    // ---- Manual upload (IDM workaround) -----------------------------------
    // When IDM (or another download manager) hijacks a download at the OS
    // layer, the recorder/runtime can't capture it. Users land the file on
    // disk themselves and drop it here; the backend wraps it in a synthetic
    // RPARun so it shows up in this list with a real audit chain.
    const [uploadOpen, setUploadOpen] = useState(false);
    const [uploadFiles, setUploadFiles] = useState([]); // File[] queued
    const [uploadMerchantId, setUploadMerchantId] = useState('');
    const [uploadAutoExtract, setUploadAutoExtract] = useState(true);
    const [uploadProgress, setUploadProgress] = useState({}); // {idx: pct}
    const [uploading, setUploading] = useState(false);
    const [dragActive, setDragActive] = useState(false);
    const [merchants, setMerchants] = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const r = await rpaFileList({
                status: statusFilter || undefined,
                search: search || undefined,
                per_page: 100,
            });
            if (r?.data?.success) {
                setFiles(r.data.data || []);
                setCounts(r.data.counts || {});
            } else {
                setError(r?.data?.message || 'Failed to load files');
            }
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setLoading(false);
        }
    }, [statusFilter, search]);

    useEffect(() => { load(); }, [load]);

    const totalAll = useMemo(() => {
        return (counts.not_extracted || 0) + (counts.extracted || 0) +
               (counts.needs_review || 0) + (counts.extract_failed || 0) +
               (counts.corrected || 0);
    }, [counts]);

    const onExtract = async (artifact) => {
        setExtracting(artifact.id);
        setError(null);
        try {
            const r = await rpaFileExtract(artifact.id);
            if (!r?.data?.success) {
                throw new Error(r?.data?.message || 'Extraction failed');
            }
            // Refresh the list so this row's status chip + linked doc update.
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        } finally {
            setExtracting(null);
        }
    };

    const onView = async (artifact) => {
        try {
            const r = await rpaFileUrl(artifact.id);
            const url = r?.data?.data?.url;
            if (url) {
                window.open(url, '_blank', 'noopener,noreferrer');
            } else {
                setError('Could not resolve file URL.');
            }
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    const onDelete = async () => {
        if (!confirmDelete) return;
        try {
            await rpaFileDelete(confirmDelete.id);
            setConfirmDelete(null);
            load();
        } catch (e) {
            setError(e?.response?.data?.message || e.message);
        }
    };

    // ---- Upload dialog handlers ------------------------------------------
    // Lazy-load merchants the first time the dialog opens — keeps the initial
    // FileList render lean for users who never need the manual upload path.
    const openUpload = useCallback(async () => {
        setUploadOpen(true);
        setUploadFiles([]);
        setUploadProgress({});
        if (merchants.length === 0) {
            try {
                const r = await rpaMerchantList({ per_page: 200 });
                if (r?.data?.success) setMerchants(r.data.data || []);
            } catch (_) {
                /* non-fatal — merchant tag is optional */
            }
        }
    }, [merchants.length]);

    const closeUpload = () => {
        if (uploading) return; // don't bail mid-upload
        setUploadOpen(false);
        setUploadFiles([]);
        setUploadProgress({});
        setDragActive(false);
    };

    const addFiles = (incoming) => {
        // Filter to file types the document pipeline can actually process,
        // dedupe by name+size, and cap to a reasonable batch.
        const allowed = ['.pdf', '.xlsx', '.xls', '.csv'];
        const valid = Array.from(incoming).filter((f) => {
            const lower = (f.name || '').toLowerCase();
            return allowed.some((ext) => lower.endsWith(ext));
        });
        if (valid.length === 0) {
            setError('Only PDF, XLSX, XLS, and CSV files are accepted.');
            return;
        }
        setUploadFiles((prev) => {
            const seen = new Set(prev.map((p) => `${p.name}::${p.size}`));
            const next = [...prev];
            for (const f of valid) {
                const key = `${f.name}::${f.size}`;
                if (!seen.has(key)) {
                    seen.add(key);
                    next.push(f);
                }
            }
            // 25 file safety cap — manual uploads are expected to be small batches
            return next.slice(0, 25);
        });
    };

    const removeUploadFile = (idx) => {
        setUploadFiles((prev) => prev.filter((_, i) => i !== idx));
        setUploadProgress((prev) => {
            const next = { ...prev };
            delete next[idx];
            return next;
        });
    };

    const onDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(true);
    };
    const onDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };
    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
    };
    const onFilePicker = (e) => {
        if (e.target.files?.length) addFiles(e.target.files);
        // Reset so picking the same file twice still fires onChange.
        e.target.value = '';
    };

    const submitUpload = async () => {
        if (uploadFiles.length === 0) return;
        setUploading(true);
        setError(null);
        let succeeded = 0;
        let failed = 0;
        for (let i = 0; i < uploadFiles.length; i += 1) {
            const f = uploadFiles[i];
            const fd = new FormData();
            fd.append('file', f);
            if (uploadMerchantId) fd.append('merchant_id', uploadMerchantId);
            fd.append('auto_extract', uploadAutoExtract ? 'true' : 'false');
            try {
                // eslint-disable-next-line no-await-in-loop
                const r = await rpaFileUpload(fd, (evt) => {
                    if (!evt?.total) return;
                    const pct = Math.round((evt.loaded / evt.total) * 100);
                    setUploadProgress((prev) => ({ ...prev, [i]: pct }));
                });
                if (r?.data?.success) {
                    succeeded += 1;
                    setUploadProgress((prev) => ({ ...prev, [i]: 100 }));
                } else {
                    failed += 1;
                    setUploadProgress((prev) => ({ ...prev, [i]: -1 }));
                }
            } catch (e) {
                failed += 1;
                setUploadProgress((prev) => ({ ...prev, [i]: -1 }));
                setError(e?.response?.data?.message || e.message);
            }
        }
        setUploading(false);
        // Refresh whether any succeeded or not — partial successes still count.
        if (succeeded > 0) {
            load();
            // Auto-close only if everything succeeded; otherwise leave the
            // dialog open so the user can see which rows failed.
            if (failed === 0) {
                setTimeout(() => closeUpload(), 600);
            }
        }
    };

    return (
        <Box>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

            <KpiGrid>
                <KpiCard
                    label="Total files"
                    value={totalAll}
                    icon={<InsertDriveFileIcon fontSize="small" />}
                    accent={RPA_COLORS.robot}
                />
                <KpiCard
                    label="Awaiting extraction"
                    value={counts.not_extracted || 0}
                    icon={<HelpOutlineIcon fontSize="small" />}
                    accent={RPA_COLORS.warning}
                    hint="Click Extract to process"
                    onClick={() => setStatusFilter('not_extracted')}
                />
                <KpiCard
                    label="Extracted clean"
                    value={(counts.extracted || 0) + (counts.corrected || 0)}
                    icon={<CheckCircleIcon fontSize="small" />}
                    accent={RPA_COLORS.success}
                    onClick={() => setStatusFilter('extracted')}
                />
                <KpiCard
                    label="Issues"
                    value={(counts.needs_review || 0) + (counts.extract_failed || 0)}
                    icon={<WarningAmberIcon fontSize="small" />}
                    accent={RPA_COLORS.danger}
                    onClick={() => setStatusFilter('needs_review')}
                />
            </KpiGrid>

            <SectionPaper>
                <Toolbar>
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="not_extracted">Not extracted</MenuItem>
                            <MenuItem value="extracted">Extracted</MenuItem>
                            <MenuItem value="needs_review">Needs review</MenuItem>
                            <MenuItem value="extract_failed">Extract failed</MenuItem>
                            <MenuItem value="corrected">Corrected</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField
                        size="small"
                        placeholder="Search filename"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{
                            startAdornment: <SearchIcon fontSize="small"
                                                        sx={{ mr: 1, color: RPA_COLORS.textFaint }} />,
                        }}
                        sx={{ minWidth: 240 }}
                    />
                    <IconButton onClick={load} disabled={loading} size="small">
                        {loading ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                    </IconButton>
                    <Box sx={{ flex: 1 }} />
                    <Tooltip title="Upload a file your download manager (e.g. IDM) intercepted">
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<CloudUploadIcon fontSize="small" />}
                            onClick={openUpload}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                background: `linear-gradient(135deg, ${RPA_COLORS.robot}, ${RPA_COLORS.accent})`,
                            }}
                        >
                            Upload file
                        </Button>
                    </Tooltip>
                    <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted, ml: 1 }}>
                        Click <strong>Extract information</strong> to push a file
                        through the document pipeline.
                    </Typography>
                </Toolbar>

                <TableShell>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>File</TableCell>
                                <TableCell>Merchant</TableCell>
                                <TableCell>Run</TableCell>
                                <TableCell>Captured</TableCell>
                                <TableCell align="right">Size</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {files.length === 0 && !loading && (
                                <TableRow>
                                    <TableCell colSpan={7} sx={{ p: 0, border: 'none' }}>
                                        <EmptyState
                                            icon={<InsertDriveFileIcon />}
                                            title="No downloaded files yet"
                                            message="When a workflow run downloads a PDF, XLSX, or CSV from a merchant portal, it shows up here. Click Extract to push the file through the document pipeline."
                                        />
                                    </TableCell>
                                </TableRow>
                            )}
                            {files.map((f) => {
                                const statusKey = f.extraction
                                    ? f.extraction.status
                                    : 'not_extracted';
                                const tone = STATUS_TONE[statusKey] || STATUS_TONE.not_extracted;
                                const isExtracting = extracting === f.id;
                                const hasDoc = !!f.extraction?.document_id;

                                return (
                                    <TableRow key={f.id} hover>
                                        <TableCell>
                                            <Stack direction="row" spacing={1} alignItems="center">
                                                {fileIcon(f.filename)}
                                                <Box sx={{ minWidth: 0 }}>
                                                    <Typography variant="body2"
                                                                sx={{ fontWeight: 600,
                                                                      wordBreak: 'break-all' }}>
                                                        {f.filename || `Artifact #${f.id}`}
                                                    </Typography>
                                                    {/* Source / lineage chip — surfaces
                                                        whether the file came from a
                                                        recording, a scheduled run, a
                                                        manual run, or was extracted from
                                                        an archive parent. */}
                                                    {(() => {
                                                        const src = fileSourceLabel(f);
                                                        if (!src) return null;
                                                        return (
                                                            <Tooltip title={src.title || ''}>
                                                                <Chip
                                                                    size="small"
                                                                    icon={src.icon || undefined}
                                                                    label={src.label}
                                                                    color={src.color}
                                                                    variant="outlined"
                                                                    sx={{
                                                                        mt: 0.25,
                                                                        height: 20,
                                                                        fontSize: 11,
                                                                        maxWidth: 320,
                                                                        '& .MuiChip-label': {
                                                                            overflow: 'hidden',
                                                                            textOverflow: 'ellipsis',
                                                                        },
                                                                    }}
                                                                />
                                                            </Tooltip>
                                                        );
                                                    })()}
                                                    {f.extraction?.amount != null && (
                                                        <Typography variant="caption"
                                                                    sx={{
                                                                        display: 'block',
                                                                        color: RPA_COLORS.textMuted,
                                                                        mt: 0.25,
                                                                    }}>
                                                            {f.extraction.vendor_name || '—'} ·{' '}
                                                            {f.extraction.reference_no || '—'} ·{' '}
                                                            {formatMoney(f.extraction.amount,
                                                                         f.extraction.currency || 'USD')}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Stack>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                                {f.merchant_name || '—'}
                                            </Typography>
                                            {/* Website / portal URL the file came from —
                                                clickable, opens in a new tab. */}
                                            {f.merchant_url && (
                                                <Typography variant="caption" sx={{
                                                    display: 'block',
                                                }}>
                                                    <a
                                                        href={f.merchant_url}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        style={{
                                                            color: RPA_COLORS.info || '#3b82f6',
                                                            textDecoration: 'none',
                                                            fontSize: 11,
                                                            wordBreak: 'break-all',
                                                        }}
                                                    >
                                                        {(() => {
                                                            try {
                                                                return new URL(f.merchant_url).host;
                                                            } catch {
                                                                return f.merchant_url;
                                                            }
                                                        })()}
                                                        <LaunchIcon sx={{
                                                            fontSize: 11,
                                                            ml: 0.25,
                                                            verticalAlign: 'middle',
                                                        }} />
                                                    </a>
                                                </Typography>
                                            )}
                                            {f.workflow_name && (
                                                <Typography variant="caption" sx={{
                                                    display: 'block',
                                                    color: RPA_COLORS.textMuted,
                                                }}>
                                                    {f.workflow_name}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption">#{f.run_id}</Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Tooltip title={formatDateLong(f.created_at) || ''}>
                                                <Box>
                                                    <Typography variant="caption" sx={{
                                                        color: RPA_COLORS.textMuted,
                                                        display: 'block',
                                                    }}>
                                                        {formatRelativeShort(f.created_at)}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{
                                                        color: RPA_COLORS.textFaint,
                                                        fontSize: 10,
                                                    }}>
                                                        {formatDateLong(f.created_at)}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                                                {formatBytes(f.size_bytes)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            {/* Tooltip carries the reason
                                                "why this row is in this
                                                state" — sourced from
                                                doc.error_message (failures)
                                                or doc.validation_issues
                                                (needs_review). The chip
                                                shows the status label;
                                                hover for detail. */}
                                            <Tooltip
                                                title={
                                                    <Box sx={{
                                                        whiteSpace: 'pre-line',
                                                        maxWidth: 360,
                                                        fontSize: 12,
                                                        lineHeight: 1.45,
                                                    }}>
                                                        {extractionReason(f)}
                                                    </Box>
                                                }
                                                placement="left"
                                                arrow
                                            >
                                                <Chip
                                                    size="small"
                                                    color={tone.color}
                                                    icon={tone.icon}
                                                    label={tone.label}
                                                    sx={{
                                                        fontWeight: 600,
                                                        cursor: 'help',
                                                    }}
                                                />
                                            </Tooltip>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                                <Tooltip title="Open file">
                                                    <IconButton size="small" onClick={() => onView(f)}>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip title={hasDoc ? 'Re-extract information' : 'Extract information'}>
                                                    <span>
                                                        <Button
                                                            size="small"
                                                            variant={hasDoc ? 'outlined' : 'contained'}
                                                            startIcon={isExtracting
                                                                ? <CircularProgress size={14} color="inherit" />
                                                                : hasDoc
                                                                ? <ReplayIcon fontSize="small" />
                                                                : <AutoFixHighIcon fontSize="small" />}
                                                            onClick={() => onExtract(f)}
                                                            disabled={isExtracting}
                                                            sx={{ textTransform: 'none', fontWeight: 600,
                                                                  ml: 0.5,
                                                                  ...(hasDoc ? {} : {
                                                                      background: `linear-gradient(135deg, ${RPA_COLORS.robot}, ${RPA_COLORS.accent})`,
                                                                  }) }}
                                                        >
                                                            {isExtracting
                                                                ? 'Extracting…'
                                                                : hasDoc
                                                                ? 'Re-extract'
                                                                : 'Extract information'}
                                                        </Button>
                                                    </span>
                                                </Tooltip>
                                                {hasDoc && onJumpToDocuments && (
                                                    <Tooltip title="View extracted document">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => onJumpToDocuments(f.extraction.document_id)}
                                                        >
                                                            <CheckCircleIcon fontSize="small"
                                                                             sx={{ color: RPA_COLORS.success }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                                <Tooltip title="Delete">
                                                    <IconButton size="small" color="error"
                                                                onClick={() => setConfirmDelete(f)}>
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableShell>
            </SectionPaper>

            {/* Extraction in flight — full-row indicator (the chip already
                shows on the affected row). */}
            {extracting && <LinearProgress sx={{ mt: 1 }} />}

            <Dialog open={confirmDelete !== null} onClose={() => setConfirmDelete(null)}>
                <DialogTitle>Delete this file?</DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        Permanently removes <strong>{confirmDelete?.filename}</strong> from
                        Google Cloud Storage and from the database. If a document was
                        already extracted from this file, it will also be deleted.
                    </Typography>
                    <Alert severity="warning">This cannot be undone.</Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={onDelete}>Delete</Button>
                </DialogActions>
            </Dialog>

            {/* ---------- Manual upload (IDM workaround) ----------------- */}
            <Dialog
                open={uploadOpen}
                onClose={closeUpload}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 2 } }}
            >
                <DialogTitle sx={{ pr: 6 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CloudUploadIcon sx={{ color: RPA_COLORS.robot }} />
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                            Upload downloaded files
                        </Typography>
                    </Stack>
                    <IconButton
                        onClick={closeUpload}
                        disabled={uploading}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        Use this when a download manager (IDM, browser policy,
                        antivirus) hijacks the file before the agent can capture
                        it. Each upload becomes a synthetic run with a real audit
                        trail.
                    </Alert>

                    {/* Drop zone */}
                    <Box
                        onDragOver={onDragOver}
                        onDragEnter={onDragOver}
                        onDragLeave={onDragLeave}
                        onDrop={onDrop}
                        sx={{
                            border: '2px dashed',
                            borderColor: dragActive ? RPA_COLORS.robot : RPA_COLORS.border,
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            background: dragActive
                                ? RPA_COLORS.robotSoft
                                : RPA_COLORS.surfaceAlt,
                            transition: 'all 0.15s ease',
                            cursor: 'pointer',
                        }}
                        onClick={() => document.getElementById('rpa-file-upload-picker')?.click()}
                    >
                        <CloudUploadIcon
                            sx={{
                                fontSize: 48,
                                color: dragActive ? RPA_COLORS.robot : RPA_COLORS.textFaint,
                                mb: 1,
                            }}
                        />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {dragActive
                                ? 'Drop files here'
                                : 'Drop files here or click to browse'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted, display: 'block', mt: 0.5 }}>
                            PDF, XLSX, XLS, or CSV — up to 25 files at once
                        </Typography>
                        <input
                            id="rpa-file-upload-picker"
                            type="file"
                            multiple
                            accept=".pdf,.xlsx,.xls,.csv,application/pdf,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                            style={{ display: 'none' }}
                            onChange={onFilePicker}
                        />
                    </Box>

                    {/* Queued files */}
                    {uploadFiles.length > 0 && (
                        <>
                            <Divider sx={{ my: 2 }} />
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                                {uploadFiles.length} file{uploadFiles.length === 1 ? '' : 's'} ready
                            </Typography>
                            <List dense disablePadding sx={{ maxHeight: 240, overflow: 'auto' }}>
                                {uploadFiles.map((f, idx) => {
                                    const pct = uploadProgress[idx];
                                    const failed = pct === -1;
                                    const done = pct === 100;
                                    return (
                                        <ListItem
                                            key={`${f.name}-${idx}`}
                                            sx={{
                                                border: '1px solid',
                                                borderColor: failed
                                                    ? RPA_COLORS.danger
                                                    : done
                                                    ? RPA_COLORS.success
                                                    : RPA_COLORS.border,
                                                borderRadius: 1,
                                                mb: 0.5,
                                                background: '#fff',
                                            }}
                                        >
                                            <ListItemIcon sx={{ minWidth: 36 }}>
                                                {fileIcon(f.name)}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={
                                                    <Typography variant="body2"
                                                                sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                                                        {f.name}
                                                    </Typography>
                                                }
                                                secondary={
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Typography variant="caption"
                                                                    sx={{ color: RPA_COLORS.textMuted, fontFamily: 'monospace' }}>
                                                            {formatBytes(f.size)}
                                                        </Typography>
                                                        {pct != null && pct >= 0 && pct < 100 && (
                                                            <Box sx={{ flex: 1, ml: 1 }}>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={pct}
                                                                    sx={{ height: 4, borderRadius: 1 }}
                                                                />
                                                            </Box>
                                                        )}
                                                        {done && (
                                                            <Chip
                                                                size="small"
                                                                color="success"
                                                                label="Uploaded"
                                                                icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                                                sx={{ height: 20, fontSize: 11 }}
                                                            />
                                                        )}
                                                        {failed && (
                                                            <Chip
                                                                size="small"
                                                                color="error"
                                                                label="Failed"
                                                                icon={<ErrorOutlineIcon sx={{ fontSize: 14 }} />}
                                                                sx={{ height: 20, fontSize: 11 }}
                                                            />
                                                        )}
                                                    </Stack>
                                                }
                                            />
                                            <ListItemSecondaryAction>
                                                <IconButton
                                                    edge="end"
                                                    size="small"
                                                    onClick={() => removeUploadFile(idx)}
                                                    disabled={uploading}
                                                >
                                                    <CloseIcon fontSize="small" />
                                                </IconButton>
                                            </ListItemSecondaryAction>
                                        </ListItem>
                                    );
                                })}
                            </List>
                        </>
                    )}

                    {/* Options */}
                    <Divider sx={{ my: 2 }} />
                    <Stack spacing={2}>
                        <FormControl size="small" fullWidth>
                            <InputLabel>Tag with merchant (optional)</InputLabel>
                            <Select
                                label="Tag with merchant (optional)"
                                value={uploadMerchantId}
                                onChange={(e) => setUploadMerchantId(e.target.value)}
                                disabled={uploading}
                            >
                                <MenuItem value="">— No merchant tag —</MenuItem>
                                {merchants.map((m) => (
                                    <MenuItem key={m.id} value={m.id}>
                                        {m.name}{m.base_url ? ` · ${m.base_url}` : ''}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={uploadAutoExtract}
                                    onChange={(e) => setUploadAutoExtract(e.target.checked)}
                                    disabled={uploading}
                                />
                            }
                            label={
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        Auto-extract after upload
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: RPA_COLORS.textMuted }}>
                                        Push the file through the document pipeline immediately.
                                        Disable to extract manually later.
                                    </Typography>
                                </Box>
                            }
                        />
                    </Stack>

                    {uploading && <LinearProgress sx={{ mt: 2 }} />}
                </DialogContent>

                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={closeUpload} disabled={uploading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={submitUpload}
                        disabled={uploading || uploadFiles.length === 0}
                        startIcon={
                            uploading
                                ? <CircularProgress size={14} color="inherit" />
                                : <CloudUploadIcon fontSize="small" />
                        }
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            background: `linear-gradient(135deg, ${RPA_COLORS.robot}, ${RPA_COLORS.accent})`,
                        }}
                    >
                        {uploading
                            ? `Uploading ${uploadFiles.length}…`
                            : `Upload ${uploadFiles.length || ''} ${
                                uploadFiles.length === 1 ? 'file' : 'files'
                            }`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}


// ----- helpers --------------------------------------------------------------

function fileIcon(filename) {
    const lower = (filename || '').toLowerCase();
    if (lower.endsWith('.pdf')) return <PictureAsPdfIcon sx={{ color: RPA_COLORS.danger }} />;
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls') ||
        lower.endsWith('.csv') || lower.endsWith('.tsv')) {
        return <TableChartIcon sx={{ color: RPA_COLORS.success }} />;
    }
    if (lower.endsWith('.zip') || lower.endsWith('.tar') ||
        lower.endsWith('.gz') || lower.endsWith('.tgz') ||
        lower.endsWith('.7z') || lower.endsWith('.bz2') ||
        lower.endsWith('.xz') || lower.endsWith('.rar')) {
        return <FolderZipIcon sx={{ color: RPA_COLORS.info || '#3b82f6' }} />;
    }
    return <InsertDriveFileIcon sx={{ color: RPA_COLORS.textFaint }} />;
}

// Best-effort "how long ago" for the captured timestamp. Shown
// alongside the absolute formatDateLong below so the user can pick
// whichever fits the use case better (audit vs. quick scan).
function formatRelativeShort(iso) {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const diffMs = Date.now() - t;
    if (diffMs < 0) return 'in the future';
    const min = Math.floor(diffMs / 60000);
    if (min < 1) return 'just now';
    if (min < 60) return `${min}m ago`;
    const hr = Math.floor(min / 60);
    if (hr < 24) return `${hr}h ago`;
    const dy = Math.floor(hr / 24);
    if (dy < 30) return `${dy}d ago`;
    const mo = Math.floor(dy / 30);
    if (mo < 12) return `${mo}mo ago`;
    return `${Math.floor(mo / 12)}y ago`;
}

// Compact "where this file came from" label. Three cases:
//   - extracted from an archive parent → "From archive: <name>"
//   - run.kind === 'recording'        → "Recording session"
//   - run.trigger_kind === 'scheduled' → "Scheduled run"
//   - run.trigger_kind === 'manual'    → "Manual run"
function fileSourceLabel(f) {
    if (f.parent_artifact) {
        return {
            icon: <UnarchiveIcon sx={{ fontSize: 14 }} />,
            label: `From archive: ${f.parent_artifact.filename || `#${f.parent_artifact.id}`}`,
            color: 'info',
            title: 'Extracted from a compressed archive — '
                   + 'the original archive is also in this list.',
        };
    }
    if (f.run_kind === 'recording') {
        return {
            icon: <FiberManualRecordIcon sx={{ fontSize: 14, color: '#dc2626' }} />,
            label: 'Recording',
            color: 'warning',
            title: 'Captured while the user was recording a workflow.',
        };
    }
    if (f.run_trigger === 'scheduled') {
        return {
            icon: <ScheduleIcon sx={{ fontSize: 14 }} />,
            label: 'Scheduled run',
            color: 'info',
            title: 'Captured by a scheduled agent run.',
        };
    }
    if (f.run_trigger === 'manual') {
        return {
            icon: null,
            label: 'Manual run',
            color: 'default',
            title: 'Captured by a manually-triggered agent run.',
        };
    }
    return null;
}

function formatMoney(amount, currency = 'USD') {
    if (amount == null) return '—';
    try {
        return new Intl.NumberFormat('en-US', {
            style: 'currency', currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(amount);
    } catch {
        return `${currency} ${Number(amount).toFixed(2)}`;
    }
}
